import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'components/Notification'
import { updateUserUploads } from 'services/userUploadRequests'
import { formatDate } from '@the-platform-group/formatters/dateFormat'
import { DATE_FORMAT } from 'constants/formats'

/**
 * Default form values for manage request form
 */
const DEFAULT_FORM_VALUES = {
  customer: null,
  assignee: null,
  status: '',
  priority: '',
  expectedCompletionDate: null,
  internalComments: '',
  customerComments: '',
}

// Mapping of form fields to request payload structure
const fieldMapping = [
  { key: 'assignee', transform: val => ({ assigneeId: val.id || val.value }) },
  { key: 'status', transform: val => ({ status: val }) },
  { key: 'priority', transform: val => ({ priority: val }) },
  {
    key: 'expectedCompletionDate',
    transform: val => ({ dueAt: formatDate(new Date(val), DATE_FORMAT) }),
  },
  {
    key: 'internalComments',
    transform: val => ({ internalComments: val }),
    includeUndefined: true,
  },
  {
    key: 'customerComments',
    transform: val => ({ customerComments: val }),
    includeUndefined: true,
  },
]

/**
 * Transform form data to request payload format
 * @param {object} formData - form data from react-hook-form
 * @returns {object} formatted request data for API
 */
const transformFormDataToRequestPayload = formData => {
  return fieldMapping.reduce((payload, { key, transform, includeUndefined }) => {
    const value = formData[key]
    const shouldInclude = includeUndefined ? value !== undefined : value

    if (shouldInclude) {
      Object.assign(payload, transform(value))
    }

    return payload
  }, {})
}

/**
 * Transform request data to form values format
 * @param {object} request - request data from API
 * @returns {object} formatted form values
 */
const transformRequestToFormValues = request => {
  if (!request) {
    return DEFAULT_FORM_VALUES
  }

  return {
    customer: request.customer
      ? {
          id: request.customer.id,
          name: request.customer.name,
          label: request.customer.name,
          value: request.customer.id,
        }
      : null,
    assignee: request.assignee
      ? {
          id: request.assignee.id,
          label: `${request.assignee.firstName} ${request.assignee.lastName}`,
          value: request.assignee.id,
        }
      : null,
    status: request.status || '',
    priority: request.priority || '',
    expectedCompletionDate: request.dueAt ? new Date(request.dueAt) : null,
    internalComments: request.internalComments || '',
    customerComments: request.customerComments || '',
  }
}

/**
 * Hook for managing the manage request form
 * @param {function} onClose - callback function to close the modal
 * @param {object} request - the request data to manage
 * @returns {object} form controls and handlers
 */
function useManageRequestForm(onClose, request) {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()

  const formMethods = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  })

  const { control, handleSubmit, formState, setValue, reset } = formMethods

  // Initialize form with request data
  useEffect(() => {
    if (request) {
      const formValues = transformRequestToFormValues(request)
      reset(formValues)
    }
  }, [request, reset])

  const handleClose = () => {
    reset()
    onClose()
  }

  const updateRequestMutation = useMutation({
    mutationFn: async formData => {
      const requestData = transformFormDataToRequestPayload(formData)
      return updateUserUploads(request.id, requestData)
    },
    onSuccess: () => {
      // Invalidate and refetch the upload requests query
      queryClient.invalidateQueries({ queryKey: ['useUploadRequestsQuery'] })

      createNotification({
        message: 'Request updated successfully',
        variant: 'success',
      })

      handleClose()
    },
    onError: error => {
      createNotification({
        message: 'Failed to update request. Please try again later.',
        variant: 'error',
      })
    },
  })

  const handleFormSubmit = handleSubmit(formData => {
    updateRequestMutation.mutate(formData)
  })

  return {
    control,
    formState,
    setValue,
    handleSubmit: handleFormSubmit,
    handleClose,
    isSubmitting: updateRequestMutation.isPending,
  }
}

export { useManageRequestForm }
